import { useState, useRef, useEffect } from "react";
import { gql, useMutation } from "@apollo/client";
import { Link } from "react-router-dom";
import { Box, TextField, Typography, IconButton, Menu, MenuItem } from "@mui/material";
import { styled } from "@mui/material/styles";
import SendIcon from "@mui/icons-material/Send";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import { convertAllDates, handleTextReplacement } from "../../utils/function";
import { useAppDispatch } from "../../app/hooks";
import { showErrorSnackbar, showSuccessSnackbar } from "../../features/snackbar/snackbarSlice";
import { IConversation, IMessage } from "../../types/twilio";
import { IRegistrant } from "../../types/registrant";
import { IUser } from "../../types/user";
import { IProject } from "../../types/project";
import { useSelector } from "react-redux";
import { selectUser } from "../../features/auth/authSlice";
import { selectProcess } from "../../features/registrants/registrantsSlice";

const Conversation = (props: ChildProps) => {
  const storeDispatch = useAppDispatch();
  const user = useSelector(selectUser);
  const process = useSelector(selectProcess);
  const { conversationDetails, messages, registrant, selectedUser, project, step, handleCompleteStep } = props;

  const [conversation, setConversation] = useState<IMessage[]>(messages ? messages : []);
  const [text, setText] = useState<string>("");
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);
  const divRef: any = useRef();

  useEffect(() => {
    if (divRef.current) {
      divRef.current.scrollIntoView({ block: "end", inline: "end" });
    }
  }, [divRef, messages]);

  const [sendText, { loading }] = useMutation(TEXTREGISTRANT, {
    onCompleted: (data) => {
      if (step && handleCompleteStep) {
        handleCompleteStep(
          {
            user: {
              _id: user?._id,
            },
            completed: new Date(),
            _id: step,
          },
          process
        );
      }
      if (data.sendText) {
        setText("");
        setConversation([...conversation, data.sendText]);
        storeDispatch(showSuccessSnackbar("Text Message Sent!"));
      }
    },
    onError: (e) => {
      console.log(e, "e");
      storeDispatch(showErrorSnackbar(e.message));
    },
  });

  const submitText = (e: any) => {
    if ((e.key === "Enter" || e.type === "click") && !e.shiftKey) {
      const rawPhone = registrant?.primaryPhone || conversationDetails?.phone;
      if (!rawPhone) return storeDispatch(showErrorSnackbar("This registrant does not have a phone number"));
      let phone = rawPhone.replace(/\D/g, "");
      if (phone?.length < 10 && phone?.length > 11) {
        return storeDispatch(showErrorSnackbar("Only US/Canadian Phone Numbers are allowed"));
      }

      sendText({
        variables: {
          project: project ? project._id : null,
          user: user?._id,
          registrant: registrant ? registrant._id : null,
          customerPhone: parseInt(phone, 10),
          message: text,
          conversation: conversationDetails?.conversationSid,
        },
      });
      e.preventDefault();
    }
  };

  const disabled = () => {
    if (registrant && registrant.textUnsubscribed !== null) {
      return true;
    }

    return false;
  };

  const handleSmsReplacement = async (sms: string) => {
    let smsReplacement = await handleTextReplacement(sms, registrant!, user!, registrant?.project!);
    setText(smsReplacement);
    setAnchorEl(null);
  };

  return (
    <Box sx={{ p: 2, border: "1px solid #000", backgroundColor: "#c8ddf1", maxHeight: "500px", overflowY: "auto", height: "100%" }}>
      <ChatContainer>
        {conversation && conversation.length ? (
          conversation.map((messageData: any, index: number) => {
            if (messageData.direction === "outbound") {
              return (
                <MessageSent key={index}>
                  <Typography sx={{ whiteSpace: "pre-line" }} dangerouslySetInnerHTML={{ __html: messageData.text }}></Typography>
                  <Box sx={{ mt: 1, fontSize: "9px", textAlign: "right" }}>
                    {messageData.author} - {convertAllDates(messageData.dateCreated, "Pp")}
                  </Box>
                </MessageSent>
              );
            } else {
              return (
                <MessageReceived key={index}>
                  <Typography sx={{ whiteSpace: "pre-line" }} dangerouslySetInnerHTML={{ __html: messageData.text }}></Typography>
                  <Box
                    sx={{ mt: 1, fontSize: "9px", cursor: "pointer", fontWeight: 500 }}
                    onClick={() =>
                      registrant
                        ? window.open(
                            `/dashboard/project/${registrant?.project._id}/registrant/${registrant._id}`,
                            "_blank",
                            "rel=noopener noreferrer"
                          )
                        : null
                    }
                  >
                    <Box>
                      {registrant ? registrant.fullName : messageData.phone}
                      {` - `}
                      {convertAllDates(messageData.dateCreated, "Pp")}
                    </Box>
                  </Box>
                </MessageReceived>
              );
            }
          })
        ) : (
          <em>No conversation has been started.</em>
        )}
        <Box sx={{ mt: "auto", position: "relative" }}>
          <TextField
            aria-label="minimum height"
            multiline
            minRows={4}
            onKeyDown={(e) => submitText(e)}
            onChange={(e) => setText(e.target.value)}
            value={text}
            placeholder={
              (registrant && registrant.textUnsubscribed) || (registrant && registrant.textUnsubscribed)
                ? "Registrant is unsubscribed"
                : `Reply to ${registrant ? registrant?.fullName : conversationDetails?.name}`
            }
            disabled={loading || disabled()}
            sx={{
              width: "100%",
              mt: 2,
              mb: messages.length > 4 ? 2 : 0,
              backgroundColor: "#fff",
              opacity: loading ? 0.5 : 1,
              borderRadius: "8px",
              "& .MuiInputBase-root": {
                px: 1,
                pt: 1,
                pb: 3,
              },
            }}
          />
          <IconButton
            onClick={(event) => setAnchorEl(event?.currentTarget!)}
            sx={{
              color: "#f6b849",
              cursor: "pointer",
              position: "absolute",
              bottom: conversation.length > 4 ? 15 : 5,
              right: 5,
              "&:hover": {
                backgroundColor: "transparent", // Or any other desired background color
              },
            }}
          >
            <BookmarkIcon />
          </IconButton>
          <IconButton
            onClick={(e) => submitText(e)}
            sx={{
              color: "#00142a;",
              cursor: "pointer",
              position: "absolute",
              bottom: conversation.length > 4 ? 15 : 5,
              right: 40,
              "&:hover": {
                backgroundColor: "transparent", // Or any other desired background color
              },
            }}
          >
            <SendIcon />
          </IconButton>
        </Box>
        <div id={"test"} ref={divRef}></div>
        <Menu
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          aria-labelledby="demo-positioned-button"
          anchorEl={anchorEl}
          open={open}
          onClose={() => setAnchorEl(null)}
        >
          {user?.texts.length ? (
            user?.texts.map((comment: any, index: number) => (
              <MenuItem
                key={index}
                onClick={() => {
                  handleSmsReplacement(comment.text);
                }}
              >
                <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", width: "500px" }}>{comment.name}</div>
              </MenuItem>
            ))
          ) : (
            <Box sx={{ p: 1 }}>
              You currently have no templates. Please click{" "}
              <Link to={`/user`} style={{ textDecoration: "none", color: "#000" }}>
                <strong>here</strong>
              </Link>{" "}
              to add one
            </Box>
          )}
        </Menu>
      </ChatContainer>
    </Box>
  );
};

interface ChildProps {
  conversationDetails: IConversation | null;
  messages: IMessage[];
  registrant: IRegistrant;
  selectedUser?: IUser | null;
  project?: IProject | null;
  step?: any;
  handleCompleteStep?: any;
}

export const ChatContainer = styled(Box)`
  --rad: 20px;
  --rad-sm: 3px;
  font: 16px/1.5 sans-serif;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

export const MessageSent = styled(Box)`
  position: relative;
  max-width: 75%;
  padding: 7px 15px;
  margin-bottom: 2px;
  border-radius: var(--rad) var(--rad-sm) var(--rad-sm) var(--rad);
  background: #00142a;
  color: #fff;
  /* moves it to the right */
  margin-left: auto;
  border-top-right-radius: var(--rad);
  margin-top: 10px;
`;

export const MessageReceived = styled(Box)`
  position: relative;
  max-width: 75%;
  padding: 7px 15px;
  margin-bottom: 2px;
  border-radius: var(--rad-sm) var(--rad) var(--rad) var(--rad-sm);
  background: #f1f1f1;
  color: #555;
  /* moves it to the left */
  margin-right: auto;
  border-top-left-radius: var(--rad);
  margin-top: 10px;
`;

const TEXTREGISTRANT = gql`
  mutation sendText(
    $project: MongoID
    $user: MongoID
    $registrant: MongoID
    $customerPhone: Float
    $message: String!
    $conversation: String
  ) {
    sendText(
      project: $project
      user: $user
      registrant: $registrant
      customerPhone: $customerPhone
      message: $message
      conversation: $conversation
    ) {
      author
      text
      dateCreated
      phone
      direction
    }
  }
`;

export default Conversation;
